// OwnershipTransfers — كشوف تسوية نقل الملكية (3.4.44): عرض + تصدير Excel.
// تُنشأ تلقائيًّا عند تغيير مالك الخيل (فوترة المعلّق على المالك الخارج).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, IconButton, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { useViewAudit } from '../../utils/viewAudit';
import api from '../../services/api';
import { SmartPage, SmartHeader } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';
import { exportStyledWorkbook } from '../../utils/xlsxStyled';
import { formatDateDisplay } from '../../utils/dateUtils';

const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function OwnershipTransfers() {
  const { t } = useTranslation();
  useViewAudit('ownership-transfers');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({}); // statement_id -> full row

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/ownership-transfers');
      setRows(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (!details[id]) {
      try {
        const res = await api.get(`/ownership-transfers/${id}`);
        setDetails((d) => ({ ...d, [id]: res.data }));
      } catch (e) { setError(e?.response?.data?.error || e.message); }
    }
  };

  const exportOne = (s) => {
    const items = (s.details || []).map((it) => ({
      [t('service', 'الخدمة')]: it.service,
      [t('record_id', 'رقم السجل')]: it.record_id,
      [t('date', 'التاريخ')]: it.date,
      [t('invoice_number', 'رقم الفاتورة')]: it.invoice_number,
      [t('amount', 'المبلغ')]: it.amount,
    }));
    exportStyledWorkbook({
      fileName: `transfer-statement-${s.statement_id}.xlsx`,
      sheetName: 'Transfer',
      rows: items.length ? items : [{ '-': '-' }],
      subtitle: `${s.horse_name || s.horse_id} · ${s.outgoing_owner_name || ''} → ${s.incoming_owner_name || ''} · ${s.transfer_date} · ${money(s.total_amount)}`,
    });
  };

  return (
    <SmartPage>
      <SmartHeader
        title={t('ownership_transfers', 'كشوف نقل الملكية')}
        subtitle={t('ownership_transfers_hint', 'تُنشأ تلقائيًّا عند تغيير مالك الخيل: تُفوتَر السجلات المعلّقة على المالك الخارج وتُحفَظ كشفًا.')}
      />
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={load} disabled={loading}>{t('refresh', 'تحديث')}</Button>
      </Stack>
      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ overflowX: 'auto', border: `1px solid ${C.line}`, borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: C.cardSoft }}>
                {['', t('horse_label', 'الخيل'), t('outgoing_owner', 'المالك الخارج'), t('incoming_owner', 'المالك الجديد'),
                  t('transfer_date', 'تاريخ النقل'), t('items', 'البنود'), t('total', 'الإجمالي'), ''].map((h, i) => (
                  <TableCell key={i} sx={{ fontWeight: 900 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ color: C.muted, py: 3 }}>{t('no_transfers', 'لا كشوف نقل بعد')}</TableCell></TableRow>
              ) : rows.map((s) => (
                <React.Fragment key={s.statement_id}>
                  <TableRow hover>
                    <TableCell><IconButton size="small" onClick={() => toggle(s.statement_id)}><KeyboardArrowDownIcon fontSize="small" sx={{ transform: openId === s.statement_id ? 'rotate(180deg)' : 'none' }} /></IconButton></TableCell>
                    <TableCell>{s.horse_name || s.horse_id}</TableCell>
                    <TableCell>{s.outgoing_owner_name || '—'}</TableCell>
                    <TableCell>{s.incoming_owner_name || '—'}</TableCell>
                    <TableCell><span className="date-ltr">{formatDateDisplay(s.transfer_date)}</span></TableCell>
                    <TableCell><Chip size="small" label={s.items_count} /></TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{money(s.total_amount)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('export', 'تصدير')}>
                        <span><IconButton size="small" onClick={() => exportOne(details[s.statement_id] || s)} disabled={openId === s.statement_id && !details[s.statement_id]}><DownloadIcon fontSize="small" /></IconButton></span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                      <Collapse in={openId === s.statement_id} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: C.cardSoft }}>
                          {!details[s.statement_id] ? <CircularProgress size={18} /> : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  {[t('service', 'الخدمة'), t('record_id', 'رقم السجل'), t('date', 'التاريخ'), t('invoice_number', 'رقم الفاتورة'), t('amount', 'المبلغ')].map((h, i) => (
                                    <TableCell key={i} sx={{ fontWeight: 700 }}>{h}</TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(details[s.statement_id].details || []).length === 0 ? (
                                  <TableRow><TableCell colSpan={5} sx={{ color: C.muted }}>{t('no_outstanding', 'لا سجلات معلّقة وقت النقل')}</TableCell></TableRow>
                                ) : details[s.statement_id].details.map((it, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{it.service}</TableCell>
                                    <TableCell>{it.record_id}</TableCell>
                                    <TableCell><span className="date-ltr">{it.date ? formatDateDisplay(it.date) : '—'}</span></TableCell>
                                    <TableCell>{it.invoice_number}</TableCell>
                                    <TableCell>{money(it.amount)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </SmartPage>
  );
}
